import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { configureAmplify } from './lib/amplify';
import { AuthProvider } from './lib/auth-state';
import { ChangeFeedWatcher } from './lib/change-feed-watcher';
import { router } from './router';
import './styles/globals.css';

configureAmplify();

// Sensible global defaults for a shop-floor tab that loses/regains focus all day. Without these,
// TanStack Query v5 defaults to staleTime 0 + refetchOnWindowFocus true, so every navigation and
// every alt-tab back into the window refetches every mounted query (and re-mints the presigned
// image URLs that back previews) — the dominant driver of the "feels laggy" symptom. Queries that
// need tighter freshness still override per-call (e.g. the 5-min S&S-backed reads); the explicit
// `refetchInterval` pollers (catalog/designs/batches) are unaffected by staleTime and keep polling.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        {/*
          Renders nothing. Reads the content-free change cursor on focus/visibility (plus a 10-min
          heartbeat while visible) and invalidates only the query keys whose domain actually moved —
          the narrow alternative to the `refetchOnWindowFocus` this file switches off above.
        */}
        <ChangeFeedWatcher />
        <RouterProvider router={router} />
      </QueryClientProvider>
    </AuthProvider>
  </StrictMode>,
);
